import { useSignal } from "@preact/signals";
import type { PageArgs } from "bunfx";
import { navigateTo, RedirectError } from "bunfx";
import { IcoChevronLeft } from "../../components/icons";
import { AppLayout } from "../../components/layout";
import {
  Button,
  Form,
  type FormErrors,
  ImageUpload,
  Input,
} from "../../components/ui";
import { usePageTitle } from "../../hooks";
import { rpc } from "../../rpc";
import { showError } from "../../stores/errors";
import type { MimicState } from "../../stores/mimic";
import type { User } from "../../types";
import { getErrorMessage } from "../../utils/errors";

type NewOrgState = {
  currentUser: User;
  mimic: MimicState;
};

export async function load(): Promise<NewOrgState> {
  const authResult = await rpc.auth.me({});

  if (!authResult.user) {
    throw new RedirectError("/login");
  }

  return {
    currentUser: authResult.user as User,
    mimic: authResult.mimic ?? null,
  };
}

export function Page({ state }: PageArgs<typeof load>) {
  usePageTitle("New Workspace");
  const { currentUser, mimic } = state.value;
  const isSubmitting = useSignal(false);
  const errors = useSignal({}) as FormErrors;
  const logoFile = useSignal<File | null>(null);

  async function handleSubmit(e: Event) {
    e.preventDefault();
    if (isSubmitting.value) return;

    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const name = formData.get("name") as string;

    if (!name.trim()) {
      errors.value = { name: "Name is required" };
      return;
    }

    isSubmitting.value = true;
    errors.value = {};

    try {
      // TODO: Upload logoFile.value to S3 and get URL
      const logoUrl = undefined; // Will be set once S3 upload is implemented

      const result = await rpc.orgs.create({
        name: name.trim(),
        logoUrl,
      });
      // Store in localStorage so home page shows this org
      localStorage.setItem("pulse:orgId", result.organization.id);
      navigateTo("/");
    } catch (err) {
      showError(getErrorMessage(err));
    } finally {
      isSubmitting.value = false;
    }
  }

  return (
    <AppLayout user={currentUser} mimic={mimic}>
      <div class="flex justify-center" data-brand="blue">
        <div class="w-full max-w-md flex flex-col gap-6">
          <a
            href="/"
            class="inline-flex items-center gap-1 text-sm font-medium text-text-muted hover:text-text"
          >
            <IcoChevronLeft class="-ml-[5px] size-4" />
            Back
          </a>

          <div>
            <h1 class="text-2xl font-bold text-text mb-2">
              Create Organization
            </h1>
            <p class="text-text-muted">
              Organizations are where you manage your team and forms.
            </p>
          </div>

          <Form
            errors={errors}
            onSubmit={handleSubmit}
            class="flex flex-col gap-4"
          >
            <Input
              label="Organization Name"
              name="name"
              placeholder="Sunday Hiking Club"
              autofocus
              required
            />

            <div class="flex flex-col gap-1">
              <span class="text-sm font-medium text-text-secondary">
                Logo <span class="font-normal text-text-muted">(optional)</span>
              </span>
              <ImageUpload
                file={logoFile}
                label="Click to upload"
                helpText="PNG, JPG, or GIF up to 2MB"
              />
            </div>

            <Button
              type="submit"
              variant="primary"
              class="w-full"
              disabled={isSubmitting.value}
            >
              {isSubmitting.value ? "Creating..." : "Create Organization"}
            </Button>
          </Form>
        </div>
      </div>
    </AppLayout>
  );
}
